import React, { useEffect, useState } from 'react';
import { View, Pressable } from 'react-native';
import {
  Card, CardHead, CardTitle, CardSub, CardBody, Chip, Input, Select, Field,
  Button, Modal, Row, Col, Spread, Grow, Txt, Icon, Screen,
} from '../components';
import { useTheme } from '../theme';
import { inr } from '../data/format';
import { getMenu } from '../services/orders/order';
import * as ma from '../services/menu/menuAdmin';
import type { DishInput, FoodType, Section } from '../services/menu/menuAdmin';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

const FOOD_TYPES: { label: string; value: FoodType }[] = [
  { label: 'Veg', value: 'veg' }, { label: 'Non-veg', value: 'non_veg' },
  { label: 'Egg', value: 'egg' }, { label: 'Vegan', value: 'vegan' },
];
const SPICE = [{ label: 'None', value: '0' }, { label: 'Mild', value: '1' }, { label: 'Medium', value: '2' }, { label: 'Hot', value: '3' }];

interface Dish { id: string; name: string; price: number; sections: string[] }
const emptyDish: DishInput = { name: '', price: 0, foodType: 'veg', allergens: [] };

export default function MenuSettings() {
  const t = useTheme();
  const c = t.colors;
  const [itemTypeId, setItemTypeId] = useState('');
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [labels, setLabels] = useState<Section[]>([]);
  const [menus, setMenus] = useState<ma.MenuGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Dish editor modal state
  const [editing, setEditing] = useState<{ id?: string; form: DishInput; sectionId: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [newSection, setNewSection] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newMenu, setNewMenu] = useState('');

  async function refresh() {
    const [menuItems, secs, labs, mns] = await Promise.all([
      getMenu().catch(() => []), ma.listSections().catch(() => []),
      ma.listLabels().catch(() => []), ma.listMenus().catch(() => []),
    ]);
    setDishes(menuItems.map((m) => ({ id: m.id, name: m.name, price: m.price, sections: m.sections ?? [] })));
    setSections(secs); setLabels(labs); setMenus(mns);
    setLoading(false);
  }

  useEffect(() => {
    // Ensure the venue's "Menu Item" type exists, then load everything.
    ma.provisionMenu()
      .then((r: any) => setItemTypeId(r?.data?.id ?? ''))
      .catch(() => undefined)
      .finally(refresh);
  }, []);

  async function saveDish() {
    if (!editing) return;
    const { form, id, sectionId } = editing;
    if (!form.name.trim() || form.price <= 0) { handleApiError(new Error('Name and a price are required'), 'Cannot save dish'); return; }
    setSaving(true);
    try {
      let dishId = id;
      if (dishId) { await ma.updateDish(dishId, form); }
      else { const created = await ma.createDish(itemTypeId, form); dishId = created.id; }
      await ma.publishDish(dishId!); // gate enforces food_type + allergens
      if (sectionId) await ma.setDishSections(dishId!, [sectionId]);
      showSuccess(null, id ? 'Dish updated' : 'Dish added');
      setEditing(null);
      await refresh();
    } catch (err) { handleApiError(err, 'Could not save dish'); }
    finally { setSaving(false); }
  }

  async function addSection() {
    if (!newSection.trim()) return;
    try { await ma.createSection(newSection.trim()); setNewSection(''); await refresh(); }
    catch (err) { handleApiError(err, 'Could not add section'); }
  }
  async function addLabel() {
    if (!newLabel.trim()) return;
    try { await ma.createLabel(newLabel.trim()); setNewLabel(''); await refresh(); }
    catch (err) { handleApiError(err, 'Could not add label'); }
  }
  async function addMenu() {
    if (!newMenu.trim()) return;
    try { await ma.createMenuGroup(newMenu.trim()); setNewMenu(''); await refresh(); }
    catch (err) { handleApiError(err, 'Could not add menu'); }
  }

  const sectionOptions = [{ label: 'No section', value: '' }, ...sections.map((s) => ({ label: s.name, value: s.id }))];
  // Group dishes by section for display (matches the order-taking grouping).
  const grouped = ['', ...sections.map((s) => s.name)].map((name) => ({
    name: name || 'Unsectioned',
    items: dishes.filter((d) => (name ? d.sections.includes(name) : d.sections.length === 0)),
  })).filter((g) => g.items.length > 0);

  return (
    <Screen>
      <Col gap={16}>
      {/* DISHES */}
      <Card>
        <CardHead>
          <View><CardTitle>Dishes</CardTitle><CardSub>Stored in SK Catalog · published dishes appear on Orders</CardSub></View>
          <Button onPress={() => setEditing({ form: { ...emptyDish }, sectionId: '' })} disabled={!itemTypeId}>+ Add dish</Button>
        </CardHead>
        <CardBody style={{ gap: 14 }}>
          {loading && <Txt color="ink3">Loading…</Txt>}
          {!loading && dishes.length === 0 && <Txt color="ink3">No dishes yet — add your first one.</Txt>}
          {grouped.map((g) => (
            <Col key={g.name} gap={6}>
              <Txt size="xs" weight="bold" color="ink3">{g.name.toUpperCase()}</Txt>
              {g.items.map((d) => (
                <Pressable key={d.id} onPress={() => setEditing({ id: d.id, form: { ...emptyDish, name: d.name, price: d.price }, sectionId: sections.find((s) => d.sections.includes(s.name))?.id ?? '' })}>
                  <Spread style={{ paddingVertical: 9, paddingHorizontal: 12, borderWidth: 1, borderColor: c.line, borderRadius: 9, backgroundColor: c.card }}>
                    <Txt weight="medium">{d.name}</Txt>
                    <Txt color="ink2">{inr(d.price)}</Txt>
                  </Spread>
                </Pressable>
              ))}
            </Col>
          ))}
        </CardBody>
      </Card>

      {/* SECTIONS */}
      <Card>
        <CardHead><View><CardTitle>Sections</CardTitle><CardSub>Grills, Curries, Rice… — how the menu is grouped</CardSub></View></CardHead>
        <CardBody style={{ gap: 10 }}>
          <Row gap={8} style={{ flexWrap: 'wrap' }}>
            {sections.map((s) => (
              <Chip key={s.id} variant="neutral">
                <Row gap={6}><Txt size="sm">{s.name}</Txt>
                  <Pressable onPress={async () => { try { await ma.deleteSection(s.id); await refresh(); } catch (e) { handleApiError(e, 'Could not delete'); } }}>
                    <Icon name="x" size={12} color={c.ink3} />
                  </Pressable>
                </Row>
              </Chip>
            ))}
            {sections.length === 0 && <Txt color="ink3" size="sm">No sections yet.</Txt>}
          </Row>
          <Row gap={8}>
            <Grow><Input placeholder="New section (e.g. Grills)" value={newSection} onChangeText={setNewSection} /></Grow>
            <Button variant="outline" onPress={addSection}>Add</Button>
          </Row>
        </CardBody>
      </Card>

      {/* LABELS */}
      <Card>
        <CardHead><View><CardTitle>Labels</CardTitle><CardSub>Chef's Special, Spicy… — badges on dishes</CardSub></View></CardHead>
        <CardBody style={{ gap: 10 }}>
          <Row gap={8} style={{ flexWrap: 'wrap' }}>
            {labels.map((l) => (
              <Chip key={l.id} variant="accent">
                <Row gap={6}><Txt size="sm">{l.name}</Txt>
                  <Pressable onPress={async () => { try { await ma.deleteLabel(l.id); await refresh(); } catch (e) { handleApiError(e, 'Could not delete'); } }}>
                    <Icon name="x" size={12} color={c.accentInk} />
                  </Pressable>
                </Row>
              </Chip>
            ))}
            {labels.length === 0 && <Txt color="ink3" size="sm">No labels yet.</Txt>}
          </Row>
          <Row gap={8}>
            <Grow><Input placeholder="New label (e.g. Chef's Special)" value={newLabel} onChangeText={setNewLabel} /></Grow>
            <Button variant="outline" onPress={addLabel}>Add</Button>
          </Row>
        </CardBody>
      </Card>

      {/* MENUS */}
      <Card>
        <CardHead><View><CardTitle>Menus</CardTitle><CardSub>All-Day Dining, Bar… — a dish can be in several</CardSub></View></CardHead>
        <CardBody style={{ gap: 10 }}>
          <Row gap={8} style={{ flexWrap: 'wrap' }}>
            {menus.map((m) => (
              <Chip key={m.id} variant="neutral">
                <Row gap={6}><Txt size="sm">{m.name}</Txt>
                  <Pressable onPress={async () => { try { await ma.deleteMenuGroup(m.id); await refresh(); } catch (e) { handleApiError(e, 'Could not delete'); } }}>
                    <Icon name="x" size={12} color={c.ink3} />
                  </Pressable>
                </Row>
              </Chip>
            ))}
            {menus.length === 0 && <Txt color="ink3" size="sm">No menus yet.</Txt>}
          </Row>
          <Row gap={8}>
            <Grow><Input placeholder="New menu (e.g. All-Day Dining)" value={newMenu} onChangeText={setNewMenu} /></Grow>
            <Button variant="outline" onPress={addMenu}>Add</Button>
          </Row>
        </CardBody>
      </Card>

      {/* DISH EDITOR */}
      {editing && (
        <Modal
          title={editing.id ? 'Edit dish' : 'Add dish'}
          onClose={() => setEditing(null)}
          footer={<Row gap={8}><Grow /><Button variant="outline" onPress={() => setEditing(null)}>Cancel</Button><Button onPress={saveDish} disabled={saving}>{saving ? 'Saving…' : 'Save & publish'}</Button></Row>}
        >
          <Col gap={12}>
            <Field label="Dish name"><Input value={editing.form.name} onChangeText={(v) => setEditing({ ...editing, form: { ...editing.form, name: v } })} /></Field>
            <Row gap={12}>
              <Grow><Field label="Price (₹)"><Input keyboardType="numeric" value={editing.form.price ? String(editing.form.price) : ''} onChangeText={(v) => setEditing({ ...editing, form: { ...editing.form, price: parseFloat(v) || 0 } })} /></Field></Grow>
              <Grow><Field label="Food type"><Select value={editing.form.foodType} onValueChange={(v) => setEditing({ ...editing, form: { ...editing.form, foodType: v as FoodType } })} options={FOOD_TYPES} /></Field></Grow>
            </Row>
            <Field label="Section"><Select value={editing.sectionId} onValueChange={(v) => setEditing({ ...editing, sectionId: v })} options={sectionOptions} /></Field>
            <Field label="Spice"><Select value={editing.form.spice ?? '0'} onValueChange={(v) => setEditing({ ...editing, form: { ...editing.form, spice: v } })} options={SPICE} /></Field>
            <Field label="Allergens" hint="Tap to toggle — required by FSSAI (empty = none declared)">
              <Row gap={6} style={{ flexWrap: 'wrap' }}>
                {ma.ALLERGENS.map((a) => {
                  const on = editing.form.allergens.includes(a);
                  return (
                    <Pressable key={a} onPress={() => setEditing({ ...editing, form: { ...editing.form, allergens: on ? editing.form.allergens.filter((x) => x !== a) : [...editing.form.allergens, a] } })}>
                      <Chip variant={on ? 'accent' : 'neutral'}>{a}</Chip>
                    </Pressable>
                  );
                })}
              </Row>
            </Field>
          </Col>
        </Modal>
      )}
      </Col>
    </Screen>
  );
}
